
import classNames from 'classnames';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import If from '../If/If';
import { DisplayType } from '../Models/Enums';
import { Tooltip } from '../Tooltip/Tooltip';

export enum InputType {
	INPUT = 'input',
	TEXTAREA = 'textarea',
	CHECKBOX = 'checkbox',
	RADIO = 'radio',
	DATE = 'date',
	TIME = 'time',
	PASSWORD = 'password',
}

interface IInputWrapperProps {
	isInputGroup?: boolean;
	wrapperId?: string;
	id?: string;
	inputType?: InputType;
	inputName?: string;
	inputId?: string;
	label?: ILabelOptions | React.ReactNode;
	labelVisible?: boolean;
	className?: string;
	displayType?: DisplayType;
	isRequired?: boolean;
	staticInput?: boolean;
	tooltip?: string | React.ReactNode;
	subDescription?: string;
	errors?: string | string[];
	innerProps?: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;
	sideButton?: React.ReactNode;
	children: React.ReactNode;
	
	
}

export enum LabelPositions {
	BEFORE,
	AFTER,
}

export interface ILabelOptions {
	node?: React.ReactNode;
	text: string;
	position?: LabelPositions;
	
	
}

function isLabelOptions(options: React.ReactNode | ILabelOptions): options is ILabelOptions {
	return options !== null
		&& options !== undefined
		&& typeof options === 'object'
		&& 'text' in options;
}
export function Errors({ errors: innerErrors }: { errors?: string | string[] }) {
	if (!innerErrors) {
		return null;
	}

	if (typeof innerErrors === 'string') {
		return (
			<div className="input-group__error-text">
				<p>{innerErrors}</p>
			</div>
		);
	}

	return (
		<div className="input-group__error-text">
			{innerErrors.map((error, idx) => {
				const key = `${error}${idx}`;
				return <p key={key}>{error}</p>;
			})}
		</div>
	);
}


@observer
class InputWrapper extends React.Component<IInputWrapperProps> {
	
	

	@observable
	private focused: boolean;

	@observable
	private isHovered: boolean;

	public render() {
		
		const {
			isInputGroup,
			wrapperId,
			inputType,
			id,
			label,
			children,
			className,
			displayType,
			inputId,
			isRequired,
			staticInput,
			sideButton,
			tooltip,
			subDescription,
			errors,
			innerProps,
			labelVisible = true,
		} = this.props;

		const tooltipId = `${id}-tooltip`;
		const subDescriptionId = `${id}-sub-description`;

		let labelPosition: LabelPositions | undefined;
		let labelInnerNode: React.ReactNode;
		let labelText: string | undefined;
		if (isLabelOptions(label)) {
			labelPosition = label.position ?? LabelPositions.BEFORE;
			labelInnerNode = label.node ?? label.text;
			labelText = label.text;
		} else if (label !== undefined && label !== null) {
			labelPosition = LabelPositions.BEFORE;
			labelInnerNode = label;
			if (typeof label === 'string') {
				labelText = label;
			}
		}

		const requiredMark = (label && isRequired) ? <span className="required">*</span> : undefined;
		const labelNode = label ? <label htmlFor={inputId}>{labelInnerNode}{requiredMark}</label> : '';

		const classes = classNames(
			className,
			`input-group-${displayType ?? DisplayType.BLOCK}`,
			isRequired ? 'input-group--is-required' : null,
			tooltip ? 'input-group--tooltip' : null,
			!isInputGroup ? 'input-group' : null,
			inputType ? `input-group__${inputType}` : null,
			(errors && (typeof errors === 'string' || errors.some(e => !!e))) ? 'input-group--error' : null,
			staticInput ? 'input-group--static' : null,
		);

		const tooltipNode = tooltip ? <Tooltip id={tooltipId} content={tooltip} /> : '';
		const subDescriptionNode = (!tooltip && subDescription) ? <p id={subDescriptionId}>{subDescription}</p> : '';

		return (
			<div
				id={wrapperId}
				className={classes}
				onFocus={this.onFocus}
				onBlur={this.onBlue}
				onMouseEnter={this.handleHover}
				onMouseLeave={this.handleHover}
				aria-label={labelText}
				{...innerProps}
			>
				<If condition={labelVisible && labelPosition === LabelPositions.BEFORE}>
					{labelNode}
				</If>
				{
					sideButton
						? (
							<div className="side-button-container">
								{children}
								{sideButton}
							</div>
						)
						: children
				}
				<If condition={labelVisible && labelPosition === LabelPositions.AFTER}>
					{labelNode}
				</If>
				{tooltipNode}
				{subDescriptionNode}
				<Errors errors={errors} />
			</div>
		);
		
	}

	@action private onFocus = () => {
		this.focused = true;
	};

	@action private onBlue = () => {
		this.focused = false;
	};

	@action private handleHover = () => {
		this.isHovered = !this.isHovered;
	};

	private setFocusClass = (classes?: string) => {
		if (this.focused) {
			return classNames('input-group--focus', classes);
		}
		return classes;
	};

	private setHoverClass = (classes?: string) => {
		if (this.isHovered) {
			return classNames('input-group--hover', classes);
		}
		return classes;
	};


}
export default InputWrapper;
